import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Receipt, AlertCircle, Calendar } from "lucide-react";
import { CurrentSubscriptionCard } from "./CurrentSubscriptionCard";
import { PlanHeader } from "@/components/subscription/PlanHeader";
import { PlanCardsGrid } from "@/components/subscription/PlanCardsGrid";
import { SubscriptionManagementCard } from "./SubscriptionManagementCard";
import { useSubscriptionPlans } from "@/hooks/use-subscription-plans";
import { formatDate } from "@/lib/utils";

export function SubscriptionPlans() {
  const {
    user,
    tiers,
    subscription,
    totalUsage,
    creditBalance,
    tiersLoading,
    billingInterval,
    selectedPlan,
    isAdmin,
    setBillingInterval,
    handleSelectPlan,
    handleManageBilling,
    handleCancelSubscription,
    getCurrentTierPrice,
    tierMetadata,
  } = useSubscriptionPlans();

  // Check if user is actually blocked from using the app
  // Only block if: no subscription, past_due, or truly inactive statuses
  // Note: "canceled" with canceledAt but still active until renewsAt is NOT blocked
  const isBlocked = !subscription || 
    subscription.status !== 'active';

  // Check if subscription will end soon (canceled but still active)
  const isCanceledButActive = subscription?.canceledAt && 
    subscription.status === 'active';

  return (
    <div className="space-y-8">
      {/* Show error if user has no company */}
      {!user?.companyId && (
        <div className="max-w-2xl mx-auto p-6 bg-destructive/10 border border-destructive/50 rounded-lg">
          <h3 className="text-lg font-semibold text-destructive mb-2">No Company Found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be associated with a company to manage subscriptions. 
            Please contact your administrator or create a company first.
          </p>
        </div>
      )}

      {/* Alert: Subscription Required - only show if truly blocked */}
      {user?.companyId && isBlocked && (
        <Alert className="max-w-7xl mx-auto bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <AlertTitle className="text-yellow-900 font-semibold">Subscription Required</AlertTitle>
          <AlertDescription className="text-yellow-800">
            {!subscription ? (
              <>
                You need an active subscription to access TurboClaim features. 
                {isAdmin ? ' Select a plan below to get started.' : ' Please contact your company administrator to subscribe.'}
              </>
            ) : subscription.status === 'canceled' ? (
              <>
                Your subscription has ended. 
                {isAdmin ? ' Select a plan below to reactivate your subscription.' : ' Please contact your company administrator to reactivate.'}
              </>
            ) : subscription.status === 'past_due' ? (
              <>
                Your subscription payment is past due. Please update your payment method to continue using TurboClaim. 
                {isAdmin && ' Click "Manage Billing" below to update your payment details.'}
              </>
            ) : (
              <>
                Your subscription is inactive. 
                {isAdmin ? ' Select a plan below to subscribe.' : ' Please contact your company administrator.'}
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Alert: Subscription Ending Soon - show if canceled but still active */}
      {user?.companyId && isCanceledButActive && (
        <Alert className="max-w-7xl mx-auto bg-blue-50 border-blue-200">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900 font-semibold">Subscription Ending</AlertTitle>
          <AlertDescription className="text-blue-800">
            Your subscription will end on {formatDate(subscription.renewsAt)}. 
            {isAdmin ? ' Select a plan below to continue your subscription.' : ' Please contact your company administrator to renew.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Alert: Scheduled Plan Change - show if tier change is scheduled */}
      {user?.companyId && subscription?.scheduledTierCode && (
        <Alert className="max-w-7xl mx-auto bg-blue-50 border-blue-200">
          <Calendar className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900 font-semibold">Scheduled Plan Change</AlertTitle>
          <AlertDescription className="text-blue-800">
            Your plan will change to <strong>{tierMetadata[subscription.scheduledTierCode]?.displayName || subscription.scheduledTierCode}</strong> on <strong>{formatDate(subscription.renewsAt)}</strong>.
          </AlertDescription>
        </Alert>
      )}

      {/* Current Subscription Section - Only show if active */}
      {user?.companyId && subscription && subscription.status === 'active' && (
        <CurrentSubscriptionCard
          subscription={subscription}
          tierDisplayName={tierMetadata[subscription.tierCode]?.displayName || subscription.tierCode}
          scheduledTierDisplayName={
            subscription.scheduledTierCode 
              ? tierMetadata[subscription.scheduledTierCode]?.displayName || subscription.scheduledTierCode
              : undefined
          }
          totalUsage={totalUsage}
          creditBalance={creditBalance}
        />
      )}

      {/* Plan Header with Toggle */}
      <PlanHeader
        billingInterval={billingInterval}
        onBillingIntervalChange={setBillingInterval}
      />

      {/* Plan Cards Grid */}
      <PlanCardsGrid
        tiers={tiers}
        isLoading={tiersLoading}
        billingInterval={billingInterval}
        currentTierCode={subscription?.tierCode}
        currentBillingPeriod={subscription?.billingPeriod || 'monthly'}
        currentTierPrice={getCurrentTierPrice()}
        hasCompany={!!user?.companyId}
        processingPlanCode={selectedPlan}
        onSelectPlan={handleSelectPlan}
        isAdmin={isAdmin}
        subscriptionStatus={subscription?.status}
        needsSubscription={!!(isBlocked || isCanceledButActive)}
        isCanceledButActive={!!isCanceledButActive}
      />

      {/* Subscription Management Section - Only show for active subscriptions */}
      {user?.companyId && subscription && subscription.status === 'active' && (
        <SubscriptionManagementCard
          subscription={subscription}
          onManageBilling={handleManageBilling}
          onCancelSubscription={handleCancelSubscription}
          isAdmin={isAdmin}
        />
      )}

      {/* Billing Information Section */}
      {user?.companyId && subscription && (
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Billing Information
              </CardTitle>
              <CardDescription>
                How billing works for your subscription
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-foreground mb-1">
                      Base Subscription Fee
                    </div>
                    <div>
                      You're charged a recurring {billingInterval} fee based on your selected plan.
                      This fee is billed automatically at the start of each billing period.
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-foreground mb-1">
                      Usage-Based Billing
                    </div>
                    <div>
                      Additional charges may apply based on your API token usage during the billing period.
                      Usage is metered and billed according to your plan's token rate.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-foreground mb-1">
                      Manage Billing & View Invoices
                    </div>
                    <div>
                      Use the "Manage Billing" button above to access the Stripe billing portal where you can:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>View and download invoices</li>
                        <li>Update payment methods</li>
                        <li>View billing history</li>
                        <li>Manage subscription settings</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-foreground mb-1">
                      Plan Changes
                    </div>
                    <div>
                      <strong>Upgrades:</strong> Take effect immediately with prorated billing adjustments.
                      <br />
                      <strong>Downgrades:</strong> Take effect at the end of your current billing period. You keep full access until then.
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
